@if (visible()) {
  <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
  <div class="cid-backdrop" (click)="close()">
    <div
      class="cid-dialog"
      role="dialog"
      data-testid="ci-dialog"
      aria-modal="true"
      aria-labelledby="cid-title"
      cdkTrapFocus
      [cdkTrapFocusAutoCapture]="true"
      (click)="$event.stopPropagation()"
      (keydown.escape)="onEscape($event)"
    >
      <to-section-title
        headingId="cid-title"
        [title]="'classicImport.title' | translate"
        [subtitle]="'classicImport.subtitle' | translate"
      >
        <button
          right
          type="button"
          class="cid-header__close"
          data-testid="ci-close"
          [disabled]="busy() || committing()"
          [attr.aria-label]="'classicImport.close' | translate"
          (click)="close()"
        >
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      </to-section-title>

      <div class="cid-body">
        <ol class="cid-steps" [attr.aria-label]="'classicImport.stepProgress' | translate">
          @for (s of steps; track s.step; let last = $last) {
            @let skipped = s.key === 'users' && usersSkipped();
            <li
              class="cid-steps__item"
              [class.cid-steps__item--skipped]="skipped"
              [attr.aria-current]="step() === s.step ? 'step' : null"
            >
              <span class="cid-steps__dot" [class.cid-steps__dot--active]="step() >= s.step" aria-hidden="true">
                {{ s.step }}
              </span>
              <span class="cid-steps__label" [class.cid-steps__label--active]="step() >= s.step">
                {{ 'classicImport.steps.' + s.key | translate }}
              </span>
              @if (skipped) {
                <span class="sr-only">{{ 'classicImport.users.stepSkipped' | translate }}</span>
              }
            </li>
            @if (!last) {
              <li class="cid-steps__line" [class.cid-steps__line--active]="step() > s.step" aria-hidden="true"></li>
            }
          }
        </ol>

        @if (errorText(); as text) {
          <p class="cid-error" role="alert" data-testid="ci-error">{{ text }}</p>
        } @else if (errorKey(); as key) {
          <p class="cid-error" role="alert" data-testid="ci-error">{{ key | translate }}</p>
        }

        <!-- [hidden] statt @if: Die Dropzone behält nach „Zurück“ die gewählte Datei passend zum Bericht. -->
        <div class="cid-file" [hidden]="step() !== 1">
          <app-file-dropzone
            data-testid="ci-dropzone"
            [accept]="acceptedFileTypes"
            [hint]="'classicImport.upload.hint' | translate"
            [disabled]="busy()"
            [busy]="busy()"
            (fileSelected)="onFileSelected($event)"
            (cleared)="onFileCleared()"
          />
          @if (busy() && step() === 1) {
            <p class="cid-checking" data-testid="ci-checking" role="status">
              <mat-spinner diameter="16" />
              {{ 'classicImport.upload.checking' | translate }}
            </p>
          }
          @if (importState(); as state) {
            <app-classic-import-report data-testid="ci-report" [report]="state.report" (download)="downloadReport()" />
          }
        </div>

        @if (step() === 2) {
          @if (importState(); as state) {
            <div class="cid-selection">
              @if (state.report.blocked) {
                <app-classic-import-report [report]="state.report" [blockersOnly]="true" />
              }
              <app-classic-import-tree
                [tree]="state.tree"
                [selected]="pendingSelection()"
                [disabled]="busy()"
                (selectionChange)="pendingSelection.set($event)"
              />
              @if (busy()) {
                <p class="cid-checking" data-testid="ci-checking" role="status">
                  <mat-spinner diameter="16" />
                  {{ 'classicImport.upload.checking' | translate }}
                </p>
              }
            </div>
          }
        }

        @if (step() === 3) {
          @if (importState(); as state) {
            <app-classic-import-users
              [questions]="state.report.questions"
              [members]="members() ?? []"
              [answers]="answers()"
              [loading]="membersLoading()"
              [loadFailed]="membersFailed()"
              (answersChange)="answers.set($event)"
              (retry)="loadMembers()"
            />
          }
        }

        @if (step() === 4) {
          @if (importState(); as state) {
            @if (done()) {
              <section class="cid-done" data-testid="ci-done">
                <h3 class="cid-done__title" data-testid="ci-done-title">
                  <mat-icon aria-hidden="true">check_circle</mat-icon>
                  {{ 'classicImport.done.title' | translate }}
                </h3>
                <ul class="cid-done__counts">
                  @for (type of doneTypes(); track type) {
                    @let count = state.report.counts[type];
                    <li class="cid-done__count" [attr.data-testid]="'ci-done-count-' + type">
                      <span class="cid-done__type">{{ 'classicImport.report.counts.' + type | translate }}</span>
                      <span>{{ count?.created ?? 0 }} {{ 'classicImport.report.created' | translate }}</span>
                      <span>{{ count?.replaced ?? 0 }} {{ 'classicImport.report.replaced' | translate }}</span>
                      <span>{{ count?.skipped ?? 0 }} {{ 'classicImport.report.skipped' | translate }}</span>
                    </li>
                  }
                </ul>
                <button type="button" class="cid-link" data-testid="ci-done-report" (click)="downloadReport()">
                  <mat-icon aria-hidden="true">download</mat-icon>
                  {{ 'classicImport.done.report' | translate }}
                </button>
              </section>
            } @else {
              <div class="cid-confirm">
                @if (commitFailure(); as failed) {
                  <div class="cid-failed" role="alert" data-testid="ci-commit-failed">
                    <strong>{{ 'classicImport.errors.nothingImported' | translate }}</strong>
                    @if (failed.failure) {
                      <span>{{ failed.failure }}</span>
                      <button
                        type="button"
                        class="cid-link"
                        data-testid="ci-commit-failed-report"
                        (click)="downloadReport()"
                      >
                        <mat-icon aria-hidden="true">download</mat-icon>
                        {{ 'classicImport.done.report' | translate }}
                      </button>
                    } @else {
                      <span>{{ failed.detailKey | translate }}</span>
                    }
                  </div>
                }
                @if (state.report.blocked) {
                  <app-classic-import-report [report]="state.report" [blockersOnly]="true" />
                }
                <app-classic-import-confirm
                  [report]="state.report"
                  [tree]="state.tree"
                  [answers]="answers()"
                  [busy]="committing()"
                  (answersChange)="onConfirmAnswers($event)"
                />
              </div>
            }
          }
        }
      </div>

      <div class="cid-footer">
        @if (done()) {
          <a
            class="cid-btn cid-btn--secondary"
            data-testid="ci-done-test-cases"
            [routerLink]="['/projects', projectId(), 'test-cases']"
            (click)="close()"
          >
            {{ 'classicImport.done.testCases' | translate }}
          </a>
          <button type="button" class="cid-btn cid-btn--primary" data-testid="ci-done-close" (click)="close()">
            {{ 'classicImport.close' | translate }}
          </button>
        } @else {
          @if (commitHintKey(); as hint) {
            <p class="cid-footer__hint" data-testid="ci-commit-hint">{{ hint | translate }}</p>
          }
          @if (step() > 1) {
            <button
              type="button"
              class="cid-btn cid-btn--secondary"
              data-testid="ci-back"
              [disabled]="busy() || committing()"
              (click)="back()"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
              {{ 'classicImport.back' | translate }}
            </button>
          }
          <button
            type="button"
            class="cid-btn cid-btn--secondary"
            data-testid="ci-cancel"
            [disabled]="busy() || committing()"
            (click)="close()"
          >
            {{ 'classicImport.cancel' | translate }}
          </button>
          @if (step() < lastStep) {
            <button
              type="button"
              class="cid-btn cid-btn--primary"
              data-testid="ci-next"
              [disabled]="!canProceed()"
              (click)="next()"
            >
              {{ 'classicImport.next' | translate }}
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          } @else {
            <button
              type="button"
              class="cid-btn cid-btn--primary cid-btn--commit"
              data-testid="ci-commit"
              [disabled]="!canCommit()"
              [attr.aria-busy]="committing()"
              (click)="commit()"
            >
              @if (committing()) {
                <mat-spinner diameter="16" />
              } @else {
                <mat-icon aria-hidden="true">check</mat-icon>
              }
              {{ 'classicImport.commit' | translate }}
            </button>
          }
        }
      </div>
    </div>
  </div>
}
